/**
 * Reads a batch's stored FFU output in the scorer document form
 * (drop 1, stretch, and extras) so the admin view can show those rows.
 * Absent fields stay absent. A row of another requirement type is kept.
 */

import { z } from 'zod';

const finiteNumber = z.number().finite();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return;
  const trimmed = value.trim();
  if (trimmed === '') return;
  return value;
};

const readFiniteNumber = (value: unknown): number | undefined => {
  const parsed = finiteNumber.safeParse(value);
  if (!parsed.success) return;
  return parsed.data;
};

const storeyIndexSchema = z.union([
  finiteNumber,
  z.literal('*'),
  z.object({ from: finiteNumber, to: finiteNumber }).strip(),
]);

export type FfuStoreyIndex = z.infer<typeof storeyIndexSchema>;

export type FfuProgram = {
  type?: string;
  share?: number;
  label?: string;
};

export type FfuStorey = {
  index?: FfuStoreyIndex;
  label?: string;
  floorToFloorHeightM?: number;
  innerHeightM?: number;
  gfaM2?: number;
  elevationFromGroundM?: number;
  programs: readonly FfuProgram[];
  planId?: string;
  drawing?: string;
};

export const FFU_AREA_KEYS = [
  'gfaM2',
  'gfaAboveGroundM2',
  'livingAreaM2',
  'premisesAreaM2',
  'footprintM2',
] as const;

export type FfuAreaKey = (typeof FFU_AREA_KEYS)[number];

export type FfuAreas = Partial<Record<FfuAreaKey, number>>;

export type FfuWindowRatio = {
  value?: number;
  determinedBy?: string;
};

export type FfuPart = {
  name?: string;
  storeys: readonly FfuStorey[];
};

export type FfuRoof = {
  form?: string;
  pitchDeg?: number;
};

export type FfuConnectionPoint = {
  part?: string;
  index?: number;
};

export type FfuSharedStorey = {
  label?: string;
  at: readonly FfuConnectionPoint[];
};

export type FfuConnection = {
  relationship?: string;
  sharedStoreys: readonly FfuSharedStorey[];
};

export type FfuApartment = {
  label?: string;
  count?: number;
  areaM2?: number;
  areaMinM2?: number;
  areaMaxM2?: number;
};

export type FfuOtherArea = {
  label?: string;
  areaM2?: number;
};

export type FfuPlanScale = {
  ratio?: string;
  paper?: string;
};

export type FfuPlan = {
  id?: string;
  file?: string;
  page?: number;
  label?: string;
  storeyIndex?: FfuStoreyIndex;
  storeyKind?: string;
  scale?: FfuPlanScale;
};

/** Element row a customer baseline can place on a building part. */
export type FfuElementRequirement = {
  kind: 'element';
  buildingId?: string;
  place?: string;
  name?: string;
  share?: number | null;
  sbef?: string;
  allowed: readonly string[];
};

/** A stored requirement that is not the element row. Kept so the type is not dropped. */
export type FfuOtherRequirement = {
  kind: 'other';
  type: string;
  buildingId?: string;
  stored: Readonly<Record<string, unknown>>;
};

export type FfuRequirement = FfuElementRequirement | FfuOtherRequirement;

export type FfuBuilding = {
  id?: string;
  windowRatio?: FfuWindowRatio;
  areas: FfuAreas;
  parts: readonly FfuPart[];
  storeys: readonly FfuStorey[];
  roof?: FfuRoof;
  slabThicknessMm?: number;
  connection?: FfuConnection;
  apartments: readonly FfuApartment[];
  otherAreas: readonly FfuOtherArea[];
  plans: readonly FfuPlan[];
  requirements: readonly FfuRequirement[];
};

export type FfuOutput = {
  projectName?: string;
  description?: string;
  location?: string;
  buildings: readonly FfuBuilding[];
};

/**
 * Keys only the previous extracted-facts tree has. `buildingName`,
 * `storeyCount` and `buildingCount` are in the stored FFU output too, so they
 * cannot tell the two apart.
 */
const isOldBuilding = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  return (
    'buildingInstanceKey' in value || 'btaM2' in value || 'windowRatio' in value
  );
};

const isSchemaRequirement = (value: unknown): boolean => {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  if (value.type.trim() === '') return false;
  return !('applies_to' in value) && !('source_label' in value);
};

const isFfuBuilding = (value: unknown): boolean => {
  if (!isRecord(value) || isOldBuilding(value)) return false;
  if (typeof value.id === 'string' && value.id.trim() !== '') return true;
  if (isRecord(value.geometry) || isRecord(value.properties)) return true;
  if (Array.isArray(value.plans)) return true;
  if (!Array.isArray(value.requirements)) return false;
  return value.requirements.some(isSchemaRequirement);
};

const isOldFacts = (value: Record<string, unknown>): boolean => {
  if (!Array.isArray(value.buildings)) return false;
  if (value.buildings.length === 0) {
    return (
      'projectName' in value && !('name' in value) && 'storeyCount' in value
    );
  }
  return value.buildings.some(isOldBuilding);
};

const isFfuDocument = (value: unknown): value is Record<string, unknown> => {
  if (!isRecord(value) || !Array.isArray(value.buildings)) return false;
  if (isOldFacts(value)) return false;
  if (value.buildings.length === 0) {
    return (
      'name' in value ||
      'projectName' in value ||
      'description' in value ||
      'location' in value
    );
  }
  return value.buildings.every(isFfuBuilding);
};

const readStoreyIndex = (value: unknown): FfuStoreyIndex | undefined => {
  const parsed = storeyIndexSchema.safeParse(value);
  if (!parsed.success) return;
  return parsed.data;
};

const readProgram = (value: unknown): FfuProgram | undefined => {
  if (!isRecord(value)) return;
  const program: FfuProgram = {};
  const type = readString(value.type);
  if (type != null) program.type = type;
  const share = readFiniteNumber(value.share);
  if (share != null) program.share = share;
  const label = readString(value.label);
  if (label != null) program.label = label;
  if (program.type == null && program.share == null && program.label == null) {
    return;
  }
  return program;
};

const readPrograms = (value: unknown): FfuProgram[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const program = readProgram(item);
    return program == null ? [] : [program];
  });
};

const readStorey = (value: unknown): FfuStorey | undefined => {
  if (!isRecord(value)) return;
  const storey: FfuStorey = {
    programs: readPrograms(value.activities ?? value.programs),
  };
  const index = readStoreyIndex(value.index);
  if (index != null) storey.index = index;
  const label = readString(value.name) ?? readString(value.label);
  if (label != null) storey.label = label;
  const floorToFloorHeightM =
    readFiniteNumber(value.height) ??
    readFiniteNumber(value.floorToFloorHeightM);
  if (floorToFloorHeightM != null)
    storey.floorToFloorHeightM = floorToFloorHeightM;
  const innerHeightM =
    readFiniteNumber(value.inner_height) ??
    readFiniteNumber(value.innerHeightM);
  if (innerHeightM != null) storey.innerHeightM = innerHeightM;
  const gfaM2 =
    readFiniteNumber(value.gfa) ??
    (isRecord(value.areas)
      ? (readFiniteNumber(value.areas.gfa) ??
        readFiniteNumber(value.areas.gfaM2))
      : undefined);
  if (gfaM2 != null) storey.gfaM2 = gfaM2;
  const elevationFromGroundM =
    readFiniteNumber(value.elevation) ??
    readFiniteNumber(value.elevationFromGroundM);
  if (elevationFromGroundM != null) {
    storey.elevationFromGroundM = elevationFromGroundM;
  }
  const planId = readString(value.planId);
  if (planId != null) storey.planId = planId;
  const drawing = readString(value.drawing);
  if (drawing != null) storey.drawing = drawing;
  const hasFact =
    storey.index != null ||
    storey.label != null ||
    storey.floorToFloorHeightM != null ||
    storey.innerHeightM != null ||
    storey.gfaM2 != null ||
    storey.elevationFromGroundM != null ||
    storey.programs.length > 0 ||
    storey.planId != null ||
    storey.drawing != null;
  if (!hasFact) return;
  return storey;
};

const readStoreys = (value: unknown): FfuStorey[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const storey = readStorey(item);
    return storey == null ? [] : [storey];
  });
};

const LEGACY_AREA_KEYS: Partial<Record<FfuAreaKey, readonly string[]>> = {
  gfaM2: ['gfa'],
  gfaAboveGroundM2: ['gfa_above_ground'],
};

const readAreas = (value: unknown): FfuAreas => {
  if (!isRecord(value)) return {};
  const areas: FfuAreas = {};
  for (const key of FFU_AREA_KEYS) {
    const legacyKeys = LEGACY_AREA_KEYS[key];
    const number =
      readFiniteNumber(value[key]) ??
      (legacyKeys == null
        ? undefined
        : legacyKeys
            .map((legacyKey) => readFiniteNumber(value[legacyKey]))
            .find((parsed) => parsed != null));
    if (number != null) areas[key] = number;
  }
  return areas;
};

const readWindowRatio = (value: unknown): FfuWindowRatio | undefined => {
  const bare = readFiniteNumber(value);
  if (bare != null) return { value: bare };
  if (!isRecord(value)) return;
  const ratio: FfuWindowRatio = {};
  const ratioValue = readFiniteNumber(value.value);
  if (ratioValue != null) ratio.value = ratioValue;
  const determinedBy = readString(value.determined_by);
  if (determinedBy != null) ratio.determinedBy = determinedBy;
  if (ratio.value == null && ratio.determinedBy == null) return;
  return ratio;
};

const readPart = (value: unknown): FfuPart | undefined => {
  if (!isRecord(value)) return;
  const name = readString(value.name);
  const storeys = readStoreys(value.storeys);
  if (name == null && storeys.length === 0) return;
  const part: FfuPart = { storeys };
  if (name != null) part.name = name;
  return part;
};

const readParts = (value: unknown): FfuPart[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const part = readPart(item);
    return part == null ? [] : [part];
  });
};

const readRoof = (value: unknown): FfuRoof | undefined => {
  if (!isRecord(value)) return;
  const roof: FfuRoof = {};
  const form = readString(value.form);
  if (form != null) roof.form = form;
  const pitchDeg = readFiniteNumber(value.pitchDeg);
  if (pitchDeg != null) roof.pitchDeg = pitchDeg;
  if (roof.form == null && roof.pitchDeg == null) return;
  return roof;
};

const readConnectionPoint = (
  value: unknown,
): FfuConnectionPoint | undefined => {
  if (!isRecord(value)) return;
  const point: FfuConnectionPoint = {};
  const part = readString(value.part);
  if (part != null) point.part = part;
  const index = readFiniteNumber(value.index);
  if (index != null) point.index = index;
  if (point.part == null && point.index == null) return;
  return point;
};

const readSharedStorey = (value: unknown): FfuSharedStorey | undefined => {
  if (!isRecord(value)) return;
  const at = Array.isArray(value.at)
    ? value.at.flatMap((item) => {
        const point = readConnectionPoint(item);
        return point == null ? [] : [point];
      })
    : [];
  const label = readString(value.label);
  if (label == null && at.length === 0) return;
  const shared: FfuSharedStorey = { at };
  if (label != null) shared.label = label;
  return shared;
};

const readConnection = (value: unknown): FfuConnection | undefined => {
  if (!isRecord(value)) return;
  const relationship = readString(value.relationship);
  const sharedStoreys = Array.isArray(value.sharedStoreys)
    ? value.sharedStoreys.flatMap((item) => {
        const shared = readSharedStorey(item);
        return shared == null ? [] : [shared];
      })
    : [];
  if (relationship == null && sharedStoreys.length === 0) return;
  const connection: FfuConnection = { sharedStoreys };
  if (relationship != null) connection.relationship = relationship;
  return connection;
};

const readApartment = (value: unknown): FfuApartment | undefined => {
  if (!isRecord(value)) return;
  const apartment: FfuApartment = {};
  const label = readString(value.label);
  if (label != null) apartment.label = label;
  const count = readFiniteNumber(value.count);
  if (count != null) apartment.count = count;
  const areaM2 = readFiniteNumber(value.areaM2);
  if (areaM2 != null) apartment.areaM2 = areaM2;
  const areaMinM2 = readFiniteNumber(value.areaMinM2);
  if (areaMinM2 != null) apartment.areaMinM2 = areaMinM2;
  const areaMaxM2 = readFiniteNumber(value.areaMaxM2);
  if (areaMaxM2 != null) apartment.areaMaxM2 = areaMaxM2;
  if (
    apartment.label == null &&
    apartment.count == null &&
    apartment.areaM2 == null &&
    apartment.areaMinM2 == null &&
    apartment.areaMaxM2 == null
  ) {
    return;
  }
  return apartment;
};

const readApartments = (value: unknown): FfuApartment[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const apartment = readApartment(item);
    return apartment == null ? [] : [apartment];
  });
};

const readOtherArea = (value: unknown): FfuOtherArea | undefined => {
  if (!isRecord(value)) return;
  const area: FfuOtherArea = {};
  const label = readString(value.label);
  if (label != null) area.label = label;
  const areaM2 = readFiniteNumber(value.areaM2);
  if (areaM2 != null) area.areaM2 = areaM2;
  if (area.label == null && area.areaM2 == null) return;
  return area;
};

const readOtherAreas = (value: unknown): FfuOtherArea[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const area = readOtherArea(item);
    return area == null ? [] : [area];
  });
};

const readPlanScale = (value: unknown): FfuPlanScale | undefined => {
  if (!isRecord(value)) return;
  const scale: FfuPlanScale = {};
  const ratio = readString(value.ratio);
  if (ratio != null) scale.ratio = ratio;
  const paper = readString(value.paper);
  if (paper != null) scale.paper = paper;
  if (scale.ratio == null && scale.paper == null) return;
  return scale;
};

const readPlan = (value: unknown): FfuPlan | undefined => {
  if (!isRecord(value)) return;
  const plan: FfuPlan = {};
  const id = readString(value.id);
  if (id != null) plan.id = id;
  const file = readString(value.file);
  if (file != null) plan.file = file;
  const page = readFiniteNumber(value.page);
  if (page != null) plan.page = page;
  const label = readString(value.label);
  if (label != null) plan.label = label;
  const storeyIndex = readStoreyIndex(value.storeyIndex);
  if (storeyIndex != null) plan.storeyIndex = storeyIndex;
  const storeyKind = readString(value.storeyKind);
  if (storeyKind != null) plan.storeyKind = storeyKind;
  const scale = readPlanScale(value.scale);
  if (scale != null) plan.scale = scale;
  if (
    plan.id == null &&
    plan.file == null &&
    plan.page == null &&
    plan.label == null &&
    plan.storeyIndex == null &&
    plan.storeyKind == null &&
    plan.scale == null
  ) {
    return;
  }
  return plan;
};

const readPlans = (value: unknown): FfuPlan[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const plan = readPlan(item);
    return plan == null ? [] : [plan];
  });
};

const readAllowed = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const text = readString(item);
    return text == null ? [] : [text];
  });
};

const storedRequirementFields = (
  value: Record<string, unknown>,
): Record<string, unknown> => {
  const stored: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(value)) {
    if (key === 'type' || key === 'buildingId') continue;
    stored[key] = field;
  }
  return stored;
};

/**
 * One stored requirement. An element row whose share or constraint is not the
 * element form is kept as {@link FfuOtherRequirement} so the stored fields stay visible.
 */
const readRequirement = (value: unknown): FfuRequirement | undefined => {
  if (!isSchemaRequirement(value) || !isRecord(value)) return;
  const type = readString(value.type);
  if (type == null) return;
  const buildingId = readString(value.buildingId);
  if (type !== 'element') {
    const other: FfuOtherRequirement = {
      kind: 'other',
      type,
      stored: storedRequirementFields(value),
    };
    if (buildingId != null) other.buildingId = buildingId;
    return other;
  }
  const constraint = isRecord(value.constraint) ? value.constraint : undefined;
  const kind = constraint?.kind;
  const shareIsNumber =
    value.share == null || readFiniteNumber(value.share) != null;
  if (kind != null && kind !== 'material') {
    const other: FfuOtherRequirement = {
      kind: 'other',
      type,
      stored: storedRequirementFields(value),
    };
    if (buildingId != null) other.buildingId = buildingId;
    return other;
  }
  if (!shareIsNumber) {
    const other: FfuOtherRequirement = {
      kind: 'other',
      type,
      stored: storedRequirementFields(value),
    };
    if (buildingId != null) other.buildingId = buildingId;
    return other;
  }
  const element: FfuElementRequirement = {
    kind: 'element',
    allowed: readAllowed(constraint?.allowed),
  };
  if (buildingId != null) element.buildingId = buildingId;
  const place = readString(value.place);
  if (place != null) element.place = place;
  const name = readString(value.name);
  if (name != null) element.name = name;
  if (value.share === null) element.share = null;
  if (typeof value.share === 'number') element.share = value.share;
  const sbef = readString(value.sbef);
  if (sbef != null) element.sbef = sbef;
  return element;
};

const readRequirements = (value: unknown): FfuRequirement[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const requirement = readRequirement(item);
    return requirement == null ? [] : [requirement];
  });
};

const readBuilding = (value: unknown): FfuBuilding | undefined => {
  if (!isFfuBuilding(value) || !isRecord(value)) return;
  const geometry = isRecord(value.geometry) ? value.geometry : undefined;
  const properties = isRecord(value.properties) ? value.properties : undefined;
  const building: FfuBuilding = {
    areas: readAreas(geometry?.areas),
    parts: readParts(geometry?.parts),
    storeys: readStoreys(geometry?.storeys),
    apartments: readApartments(value.apartments),
    otherAreas: readOtherAreas(value.otherAreas),
    plans: readPlans(value.plans),
    requirements: readRequirements(value.requirements),
  };
  const id = readString(value.id);
  if (id != null) building.id = id;
  const windowRatio = readWindowRatio(properties?.window_ratio);
  if (windowRatio != null) building.windowRatio = windowRatio;
  const roof = readRoof(geometry?.roof);
  if (roof != null) building.roof = roof;
  const slabThicknessMm = readFiniteNumber(value.slabThicknessMm);
  if (slabThicknessMm != null) building.slabThicknessMm = slabThicknessMm;
  const connection = readConnection(value.connection);
  if (connection != null) building.connection = connection;
  return building;
};

const parseFfuDocument = (value: Record<string, unknown>): FfuOutput => {
  const buildings = Array.isArray(value.buildings)
    ? value.buildings.flatMap((item) => {
        const building = readBuilding(item);
        return building == null ? [] : [building];
      })
    : [];
  const output: FfuOutput = { buildings };
  const projectName = readString(value.name) ?? readString(value.projectName);
  if (projectName != null) output.projectName = projectName;
  const description = readString(value.description);
  if (description != null) output.description = description;
  const location = readString(value.location);
  if (location != null) output.location = location;
  return output;
};

/**
 * The scorer document on an aggregated batch output.
 * Reads the root when that object is the document, otherwise
 * `extractedDesignFacts` when that object is the document.
 * Returns null when the stored tree is still the previous facts shape.
 */
export const readFfuBatchOutput = (raw: unknown): FfuOutput | null => {
  if (isFfuDocument(raw)) return parseFfuDocument(raw);
  if (!isRecord(raw)) return null;
  if (!isFfuDocument(raw.extractedDesignFacts)) return null;
  return parseFfuDocument(raw.extractedDesignFacts);
};

export const formatFfuNumber = (value: number): string => {
  if (Number.isInteger(value)) return String(value);
  const rounded = Math.round(value * 10000) / 10000;
  return String(rounded);
};

export const formatFfuStoreyIndex = (index: FfuStoreyIndex): string => {
  if (typeof index === 'number') return formatFfuNumber(index);
  if (index === '*') return '*';
  return `${formatFfuNumber(index.from)}–${formatFfuNumber(index.to)}`;
};

export const formatFfuProgram = (program: FfuProgram): string => {
  const type = program.type ?? '';
  const label =
    program.label != null && program.label !== '' ? ` (${program.label})` : '';
  const share =
    program.share != null ? ` ${formatFfuNumber(program.share)}` : '';
  return `${type}${label}${share}`.trim();
};

export const formatFfuPrograms = (programs: readonly FfuProgram[]): string =>
  programs
    .map(formatFfuProgram)
    .filter((text) => text !== '')
    .join(', ');

export const formatFfuStoredValue = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  if (value == null) return '';
  return JSON.stringify(value);
};
